import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  EventEmitter,
  Input,
  OnChanges,
  Optional,
  Output,
  ViewChild,
} from '@angular/core';
import { GpuFormFieldAppearance } from '@geneplanet/ngx-ui';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, startWith } from 'rxjs';
import { AnalysesProcessingType } from 'src/app/analyses/interfaces/analyses.types';
import { BloodAddHostDirective } from '../../directives/blood-add-host.directive';
import { BloodAddBase } from '../../interfaces/blood-add-base.interface';
import { BloodConfiguration } from '../../interfaces/blood-configuration.interface';
import { BloodAnalysisResult } from '../../interfaces/blood-analysis-result.interface';
import { BloodAddChoiceComponent } from '../blood-add-choice/blood-add-choice.component';
import { BloodAddValueComponent } from '../blood-add-value/blood-add-value.component';
import { BloodAddService } from './blood-add.service';
import { LocalBloodAnalysis } from '../../interfaces/blood-local-analysis';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

enum ComponentState {
  LOADING = 'loading',
  ERROR = 'error',
  FORM = 'form',
}

@UntilDestroy()
@Component({
  selector: 'app-blood-add-host',
  templateUrl: './blood-add-host.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BloodAddHostComponent implements OnChanges {
  @ViewChild(BloodAddHostDirective, { static: true })
  bloodAddHost!: BloodAddHostDirective;

  @Input() configuration: BloodConfiguration;
  @Input() analysisResult: BloodAnalysisResult;
  @Input() backVisible = true;
  @Input() formFieldAppearance = GpuFormFieldAppearance.Card;
  @Input() editMode = false;
  @Input() local = false;

  // When saving we return resultId
  @Output() saved = new EventEmitter<string>();
  // Can be dismissed with no action, or "delete"
  @Output() closed = new EventEmitter<string | void>();

  protected isValidForm = false;
  protected state = ComponentState.LOADING;

  private componentRef: ComponentRef<BloodAddBase>;

  constructor(
    @Optional() protected modal: NgbActiveModal,
    private cdRef: ChangeDetectorRef,
    private addService: BloodAddService
  ) {}

  ngOnChanges(): void {
    // Result is only required if we're editing result
    if (this.configuration && this.editMode === !!this.analysisResult) {
      this.loadComponent(this.configuration);
    }
  }

  protected onSubmit(): void {
    const value = this.componentRef.instance.onSubmit();

    if (!value) {
      // Form is invalid
      return;
    }

    this.state = ComponentState.LOADING;
    this.bloodAddHost.viewContainerRef.clear();
    this.cdRef.markForCheck();

    if (this.local) {
      const localData: LocalBloodAnalysis = {
        name: this.configuration.longName,
        analysisId: this.configuration.id,
        result: { markers: value.markers },
      };

      if (this.editMode) {
        this.addService.updateLocal(localData);
      } else {
        this.addService.addLocal(localData);
      }

      this.saved.emit('');
    } else {
      let action: Observable<{ id: string }>;

      if (this.editMode) {
        action = this.addService.update(this.analysisResult.result.id, value);
      } else {
        action = this.addService.create(value);
      }

      action.subscribe({
        next: (response) => {
          this.saved.emit(response.id);
        },
        error: () => {
          this.state = ComponentState.ERROR;
          this.cdRef.markForCheck();
        },
      });
    }
  }

  private loadComponent(configuration: BloodConfiguration) {
    const component = this.selectComponent(configuration.processingType);

    this.state = ComponentState.FORM;

    const viewContainerRef = this.bloodAddHost.viewContainerRef;
    viewContainerRef.clear();

    this.componentRef = viewContainerRef.createComponent<BloodAddBase>(
      component
    );
    this.componentRef.instance.configuration = configuration;
    this.componentRef.instance.formFieldAppearance = this.formFieldAppearance;
    this.componentRef.instance.analysisResult = this.analysisResult;
    // Don't show date field when adding report & saving it locally
    this.componentRef.instance.showDate = !this.local;

    // Init is optional
    if (this.componentRef.instance.init) {
      this.componentRef.instance.init();
    }

    this.componentRef.instance.form.statusChanges
      .pipe(untilDestroyed(this), startWith(null))
      .subscribe(() => {
        this.isValidForm = this.componentRef.instance.form.valid;
        this.cdRef.markForCheck();
      });

    this.componentRef.instance.submitted
      .pipe(untilDestroyed(this))
      .subscribe(() => this.onSubmit());

    this.cdRef.markForCheck();
  }

  private selectComponent(processingType: AnalysesProcessingType) {
    if (processingType === AnalysesProcessingType.Qualitative) {
      return BloodAddChoiceComponent;
    }
    return BloodAddValueComponent;
  }
}
